import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { MainLayout } from '../components/layout/MainLayout';
import { championshipApi, federationApi, fileApi, scoringSystemApi, categoryTableApi, rankingFederativoApi } from '../lib/api';
// FASE 2.1 — aviso do Status "Finalizado" e atalho de premiação (o rótulo não
// premia: a medalha só nasce na premiação, que é uma ação à parte).
import { buildAwardWarning } from '../lib/warnings';
import { useAuth } from '../contexts/AuthContext';
import { ImageUpload } from '../components/common/ImageUpload';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { SecaoFormulario, CabecalhoSecao, Dica, BarraAcoes } from '../components/common/Formulario';
import { ArrowLeft, ChevronRight, Loader2, Save, Trophy, Image, FileText, Medal, Users, Swords, ShieldCheck, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

export default function ChampionshipFormPage() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [federations, setFederations] = useState([]);
  const [scoringSystems, setScoringSystems] = useState([]);
  const [categoryTables, setCategoryTables] = useState([]);
  const [pendingImage, setPendingImage] = useState(null);
  // Bloco A — o formulário passa a MOSTRAR (somente leitura) a regra que o
  // Ranking Federativo realmente aplica neste campeonato e se ele participa
  // do ranking. Nada aqui grava ou calcula pontos.
  const [federationRule, setFederationRule] = useState(null);
  const [championshipSnapshot, setChampionshipSnapshot] = useState(null);
  // FASE 2.1 — quantas medalhas o campeonato JÁ tem (somente leitura). É o que
  // permite avisar, no campo Status, que "Finalizado" não significa premiado.
  const [medalCount, setMedalCount] = useState(null);
  const [awarding, setAwarding] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    date: '',
    address: '',
    federation_id: '',
    payment_deadline: '',
    status: 'open',
    about_event: '',
    event_location: '',
    registration_values: '',
    payment_methods: '',
    prizes: '',
    rules: '',
    schedule: '',
    image_rights: '',
    health_declaration: '',
    areas: [],
    categories_info: '',
    absolute_info: '',
    checkin_info: '',
    brackets_info: '',
    weigh_in_info: '',
    banner_path: '',
    weight_table_path: '',
    edital_path: '',
    scoring_system_id: '',
    category_table_id: '',
    draw_mode: 'seeded',
    draw_ranking_source: 'global',
    draw_priority: 'ranking',
    draw_seeds: 'quarter',
    results_mode: 'manual',
    has_second_bronze: false,
    belt_competition_rule: 'orange_green_combined',
    modality: 'gi',  // gi (Kimono) | nogi (No-Gi) | misto (as duas)
    combine_modality_ranking: true,  // misto: true = pontos somados | false = rankings separados
    kids_enabled: false,  // agrupamento Kids (apenas consulta/relatorios — nao e categoria)
    kids_max_age: null,  // 5 | 7 | 9 | 11 | 13 | 15 — categorias oficiais ate essa idade pertencem ao Kids
    ranking_weight: 'low'  // peso no Ranking Federativo: low | medium | high (bonus aditivo)
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [fedRes, ssRes, ctRes] = await Promise.all([
          federationApi.list(),
          scoringSystemApi.list(),
          categoryTableApi.list()
        ]);
        setFederations(fedRes.data);
        setScoringSystems(ssRes.data);
        setCategoryTables(ctRes.data);
        
        if (isEdit) {
          const { data } = await championshipApi.get(id);
          setFormData({
            name: data.name || '',
            date: data.date || '',
            address: data.address || '',
            federation_id: data.federation_id || '',
            payment_deadline: data.payment_deadline || '',
            status: data.status || 'open',
            about_event: data.about_event || '',
            event_location: data.event_location || '',
            registration_values: data.registration_values || '',
            payment_methods: data.payment_methods || '',
            prizes: data.prizes || '',
            rules: data.rules || '',
            schedule: data.schedule || '',
            image_rights: data.image_rights || '',
            health_declaration: data.health_declaration || '',
            areas: data.areas || [],
            categories_info: data.categories_info || '',
            absolute_info: data.absolute_info || '',
            checkin_info: data.checkin_info || '',
            brackets_info: data.brackets_info || '',
            weigh_in_info: data.weigh_in_info || '',
            banner_path: data.banner_path || '',
            weight_table_path: data.weight_table_path || '',
            edital_path: data.edital_path || '',
            scoring_system_id: data.scoring_system_id || '',
            category_table_id: data.category_table_id || '',
            draw_mode: data.draw_mode || 'seeded',
            draw_ranking_source: data.draw_ranking_source || 'global',
            draw_priority: data.draw_priority || 'ranking',
            draw_seeds: data.draw_seeds || 'quarter',
            results_mode: data.results_mode || 'manual',
            has_second_bronze: !!data.has_second_bronze,
            modality: data.modality || 'gi',
            combine_modality_ranking: data.combine_modality_ranking !== false,
            kids_enabled: !!data.kids_enabled,
            kids_max_age: data.kids_max_age || null,
            // Campeonatos antigos (sem o campo) mantem o comportamento atual (baixo).
            ranking_weight: ['low', 'medium', 'high'].includes(data.ranking_weight)
              ? data.ranking_weight
              : 'low',
            // Eventos existentes sem a regra definida mantem o comportamento atual (faixas separadas).
            // Valores legados (ex.: 'laranja_verde_combined' gravado por versoes anteriores) sao
            // normalizados para o valor canonico 'orange_green_combined'.
            belt_competition_rule:
              data.belt_competition_rule === undefined ||
              data.belt_competition_rule === null ||
              data.belt_competition_rule === ''
                ? 'separated'
                : data.belt_competition_rule === 'separated'
                  ? 'separated'
                  : 'orange_green_combined'
          });
        }
      } catch (error) {
        toast.error('Erro ao carregar dados');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, isEdit]);

  // FASE 2.1 — leitura da contagem de medalhas do campeonato (o endpoint é
  // público e somente lê). Se falhar, `medalCount` fica null e o aviso do
  // Status cai no texto genérico — nunca bloqueia a tela.
  const refreshMedalCount = useCallback(async () => {
    if (!isEdit) return;
    try {
      const { data } = await championshipApi.getMedals(id);
      setMedalCount((data?.medals || []).length);
    } catch (e) {
      setMedalCount(null);
    }
  }, [id, isEdit]);

  useEffect(() => {
    refreshMedalCount();
  }, [refreshMedalCount]);

  // FASE 2.1 — atalho "Premiar agora": a premiação é ação à parte do rótulo
  // "Finalizado" e é ela que grava as medalhas (fonte do Ranking Federativo e
  // da página pública). Mesmo endpoint do botão "Finalizar" do campeonato.
  const handlePremiarAgora = async () => {
    if (!isEdit) return;
    if (!window.confirm(buildAwardWarning({ medalhasCount: medalCount || 0 }))) return;
    setAwarding(true);
    try {
      const { data } = await championshipApi.finalize(id);
      // FASE 7.1 — a premiação NUNCA é silenciosa quanto ao ranking: se o
      // campeonato não participa do Ranking Federativo (sem federação/snapshot),
      // o operador é avisado em vez de receber um "sucesso" enganoso.
      const ranking = data?.ranking_federativo;
      if (ranking && ranking.processed === false) {
        toast.warning(ranking.message || 'Medalhas gravadas, mas o Ranking Federativo NÃO foi atualizado.');
      } else {
        toast.success('Campeonato premiado — medalhas registradas');
      }
      setFormData((prev) => ({ ...prev, status: 'finished' }));
      await refreshMedalCount();
    } catch (error) {
      toast.error(
        error?.response?.data?.detail || 'Falha ao premiar o campeonato',
      );
    } finally {
      setAwarding(false);
    }
  };

  // Bloco A (S1) — regra da federação organizadora, em modo SOMENTE LEITURA.
  // Um campeonato COM federação nunca usa `scoring_system_id`: a escala de
  // medalha, a participação, os dois bônus de performance e o valor de cada
  // faixa de peso vêm desta regra (o snapshot congela a versão na entrada em
  // andamento). Aqui só mostramos a verdade — nada é gravado.
  useEffect(() => {
    const federationId = formData.federation_id;
    if (!federationId) {
      setFederationRule(null);
      return undefined;
    }
    let active = true;
    rankingFederativoApi
      .getRule(federationId)
      .then(({ data }) => { if (active) setFederationRule(data); })
      .catch(() => { if (active) setFederationRule(null); });
    return () => { active = false; };
  }, [formData.federation_id]);

  // Bloco A (S6) — snapshot congelado deste campeonato: é ele que determina se
  // o campeonato participa do Ranking Federativo (criado na entrada em
  // andamento, e nunca criado se o campeonato já tinha lutas registradas).
  useEffect(() => {
    if (!isEdit || !id) {
      setChampionshipSnapshot(null);
      return undefined;
    }
    let active = true;
    rankingFederativoApi
      .getChampionshipSnapshot(id)
      .then(({ data }) => { if (active) setChampionshipSnapshot(data?.snapshot || null); })
      .catch(() => { if (active) setChampionshipSnapshot(null); });
    return () => { active = false; };
  }, [isEdit, id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      let result;
      if (isEdit) {
        const resp = await championshipApi.update(id, formData);
        if (resp?.data?.warning) {
          toast.warning(resp.data.warning, { duration: 9000 });
        }
        toast.success('Campeonato atualizado com sucesso');
        result = { _id: id };
      } else {
        const { data } = await championshipApi.create(formData);
        result = data;
        toast.success('Campeonato criado com sucesso');
      }
      
      // Upload pending image if exists
      if (pendingImage && result._id) {
        try {
          await fileApi.upload(pendingImage, 'championship', result._id);
        } catch (err) {
          toast.error('Erro ao enviar imagem do banner');
        }
      }
      // Upload pending weight table (PDF or image)
      if (pendingWeightTable && result._id) {
        try {
          await fileApi.upload(pendingWeightTable, 'championship', result._id, 'weight_table_path');
        } catch (err) {
          toast.error('Erro ao enviar tabela de pesos');
        }
      }
      // Upload pending edital (PDF)
      if (pendingEdital && result._id) {
        try {
          await fileApi.upload(pendingEdital, 'championship', result._id, 'edital_path');
        } catch (err) {
          toast.error('Erro ao enviar edital do campeonato');
        }
      }
      
      navigate('/championships');
    } catch (error) {
      toast.error(isEdit ? 'Erro ao atualizar campeonato' : 'Erro ao criar campeonato');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleImageUpload = (pathOrFile) => {
    if (pathOrFile instanceof File) {
      setPendingImage(pathOrFile);
    } else {
      setFormData(prev => ({ ...prev, banner_path: pathOrFile }));
    }
  };

  const [pendingWeightTable, setPendingWeightTable] = useState(null);
  const handleWeightTableUpload = (pathOrFile) => {
    if (pathOrFile instanceof File) {
      setPendingWeightTable(pathOrFile);
    } else {
      // pathOrFile is either the stored path (string) or null (removed)
      setFormData(prev => ({ ...prev, weight_table_path: pathOrFile || '' }));
    }
  };

  const [pendingEdital, setPendingEdital] = useState(null);
  const handleEditalUpload = (pathOrFile) => {
    if (pathOrFile instanceof File) {
      setPendingEdital(pathOrFile);
    } else {
      // pathOrFile is either the stored path (string) or null (removed)
      setFormData(prev => ({ ...prev, edital_path: pathOrFile || '' }));
    }
  };

  const selectedScoring = scoringSystems.find(s => s._id === formData.scoring_system_id);

  // Regra Laranja + Verde: verde = LIGADO (combinado), cinza = DESLIGADO (separado)
  const beltRuleCombined = (formData.belt_competition_rule || 'orange_green_combined') === 'orange_green_combined';

  // Modalidade: gi (Kimono) | nogi (No-Gi) | misto (as duas no mesmo evento)
  const isMisto = (formData.modality || 'gi') === 'misto';

  // ---------------------------------------------------------------------------
  // Bloco A — leitura/rotulagem do que o Ranking Federativo aplica.
  // Antes o formulário exibia e pedia decisões de pontuação que o ranking
  // federado IGNORA (escala de medalha, valor do 2º Bronze, valor do peso).
  // Nada aqui altera pontos: é rótulo, leitura e aviso.
  // ---------------------------------------------------------------------------
  const federationSelected = !!formData.federation_id;
  const selectedFederation = federations.find((f) => f._id === formData.federation_id);
  const ruleConfig = federationRule?.config || null;
  const ruleVersion = federationRule?.version ?? null;
  const ruleHasSavedVersion = !!federationRule?.exists;
  const ruleWeightPoints = {
    low: ruleConfig?.weight_points?.low ?? 1,
    medium: ruleConfig?.weight_points?.medium ?? 3,
    high: ruleConfig?.weight_points?.high ?? 5,
  };
  const ruleSecondBronze = ruleConfig?.medal_points?.second_bronze ?? 0;
  const canEditRules = ['super_admin', 'federation'].includes(user?.role);

  const WEIGHT_LABELS = { none: 'Sem peso', low: 'Baixo', medium: 'Médio', high: 'Alto' };
  const STATUS_LABELS = {
    open: 'Inscrições Abertas',
    closed: 'Inscrições Encerradas',
    in_progress: 'Em Andamento',
    finished: 'Finalizado',
  };
  const weightLevel = formData.ranking_weight || 'low';
  const effectiveWeightPoints = weightLevel === 'none'
    ? 0
    : (ruleWeightPoints[weightLevel] ?? 0);
  const weightOptionLabel = (level) => {
    if (level === 'none') return 'Sem peso (não soma bônus de peso)';
    if (!federationSelected) return `${WEIGHT_LABELS[level]} (só tem efeito com federação organizadora)`;
    const pts = ruleWeightPoints[level] ?? 0;
    return pts === 0
      ? `${WEIGHT_LABELS[level]} — faixa desativada na regra da federação (0 ponto)`
      : `${WEIGHT_LABELS[level]} — +${pts} ponto${pts === 1 ? '' : 's'} (regra da federação)`;
  };
  const teamSourceLabel = {
    medals_only: 'Somente medalha (padrão) — só medalhistas, por pontos de medalha',
    athletes_with_fights: 'Somente atletas que lutaram (por pontuação)',
    all_ranked_athletes: 'Todos os atletas pontuados no ranking (por pontuação)',
  }[ruleConfig?.team_points_source || 'medals_only'];
  // Etapa 3 — a agregação do Ranking Federativo é SEMPRE aditiva (soma dos
  // totais por campeonato). O campo `performance_aggregation` das regras
  // antigas continua sendo LIDO no banco, mas não altera mais nada: exibir
  // "soma ÷ campeonatos" aqui seria mentir para quem opera.
  const aggregationLabel = 'Soma dos campeonatos — bônus acumulado (sem média)';
  const includeWalkovers = ruleConfig?.include_walkovers !== false;
  const TONE_CLASSES = {
    ok: 'bg-emerald-500/10 border-emerald-500/40 text-emerald-300',
    warn: 'bg-amber-500/10 border-amber-500/40 text-amber-300',
    muted: 'bg-[#1A1A1A] border-[#27272A] text-[#A1A1AA]',
  };

  // Selo "participa / não participa do Ranking Federativo" + motivo real.
  const rankingParticipation = (() => {
    if (!federationSelected) {
      return {
        tone: 'muted',
        label: 'Não participa do Ranking Federativo',
        reason: 'Sem federação organizadora: este campeonato usa o fluxo legado (Sistema de Pontuação + ranking global).',
      };
    }
    if (championshipSnapshot) {
      const snapped = WEIGHT_LABELS[championshipSnapshot.weight] || championshipSnapshot.weight || '—';
      // O valor do peso vem da REGRA CONGELADA no próprio snapshot — não da
      // regra vigente hoje, que pode ter mudado desde a entrada em andamento.
      const snappedWeightPoints = championshipSnapshot.rule?.weight_points || ruleWeightPoints;
      const snappedPoints = championshipSnapshot.weight === 'none'
        ? 0
        : (snappedWeightPoints[championshipSnapshot.weight] ?? 0);
      return {
        tone: 'ok',
        label: `Participa do Ranking Federativo — regra congelada (v${championshipSnapshot.rule_version ?? '?'})`,
        reason: `O snapshot deste campeonato já foi criado, então ele entra no ranking federado. Peso congelado: ${snapped} (+${snappedPoints} ponto${snappedPoints === 1 ? '' : 's'}).`,
      };
    }
    if (['in_progress', 'finished'].includes(formData.status)) {
      return {
        tone: 'warn',
        label: 'Sem snapshot — NÃO entra no Ranking Federativo',
        reason: 'O snapshot é criado no momento em que o campeonato entra em andamento. Se ele já possuía lutas registradas nesse momento, o snapshot não é criado — os resultados não são reinterpretados por uma regra posterior (integridade histórica).',
      };
    }
    return {
      tone: 'muted',
      label: 'Ainda não participa do Ranking Federativo',
      reason: `O snapshot da regra é criado quando o campeonato entra em andamento (status atual: ${STATUS_LABELS[formData.status] || formData.status}).`,
    };
  })();
  const snapshotFederationMismatch = !!(
    championshipSnapshot && federationSelected && championshipSnapshot.federation_id !== formData.federation_id
  );

  // Bloco C — como o Ranking Federativo trata a MODALIDADE deste campeonato é
  // decidido no snapshot (congelado na entrada em andamento). Snapshot anterior
  // ao Bloco C não tem o campo: vale o histórico (misto = uma linha só).
  const MODALITY_RANKING_LABELS = {
    single: 'modalidade única do evento',
    combined: 'uma única linha por atleta (Kimono + No-Gi somados)',
    split: 'uma linha por atleta POR MODALIDADE (Kimono e No-Gi separados)',
  };
  const frozenModalityRanking = championshipSnapshot
    ? (championshipSnapshot.modality_ranking
      || (championshipSnapshot.modality === 'misto' ? 'combined' : 'single'))
    : null;
  const frozenModalityRankingLabel = frozenModalityRanking
    ? `Snapshot já congelado neste campeonato: ${MODALITY_RANKING_LABELS[frozenModalityRanking] || frozenModalityRanking}. Mudar esta opção agora não reescreve o que já foi congelado.`
    : null;

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" aria-hidden="true" /><span role="status" aria-live="polite" className="ml-2 text-sm text-[#A1A1AA]">Carregando...</span>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="championship-form-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/championships')}
            className="text-[#A1A1AA] hover:text-white"
          >
            <ArrowLeft size={20} />
          </Button>
          <div>
            {/* Trilha de contexto (Etapa Visual 2) */}
            <nav aria-label="Você está em" className="mb-2 flex flex-wrap items-center gap-1.5 text-xs text-[#71717A]">
              <Link to="/championships" className="transition-colors hover:text-[#D4AF37]">Campeonatos</Link>
              <ChevronRight size={12} aria-hidden="true" className="opacity-60" />
              <span>{isEdit ? 'Editar campeonato' : 'Novo campeonato'}</span>
            </nav>
            <h1 className="text-3xl font-bold text-white font-['Chivo']">
              {isEdit ? 'Editar Campeonato' : 'Novo Campeonato'}
            </h1>
            <p className="text-[#A1A1AA] mt-1">
              {isEdit ? 'Atualize as informações do campeonato' : 'Preencha os dados para criar um novo campeonato'}
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <p className="text-xs text-[#A1A1AA]"><span className="text-[#D4AF37]">*</span> campos obrigatórios</p>
          <Tabs defaultValue="basic" className="w-full">
            <TabsList className="bg-[#141414] border border-[#27272A] p-1 flex-wrap h-auto">
              <TabsTrigger value="basic" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
                <Trophy size={16} className="mr-2" /> Basico
              </TabsTrigger>
              <TabsTrigger value="details" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
                <FileText size={16} className="mr-2" /> Detalhes
              </TabsTrigger>
              <TabsTrigger value="media" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
                <Image size={16} className="mr-2" /> Imagem
              </TabsTrigger>
            </TabsList>

            {/* Basic Info Tab */}
            <TabsContent value="basic" className="mt-6">
              <Card className="bg-[#141414] border-[#27272A]">
                <CardHeader>
                  <CardTitle className="text-lg text-white font-['Chivo'] flex items-center gap-2">
                    <Trophy size={20} className="text-[#D4AF37]" />
                    Informações Básicas
                  </CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2 md:col-span-2">
                    <Label className="text-[#A1A1AA]" htmlFor="championship-name">Nome do Campeonato *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      placeholder="Ex: Copa Brasil de Jiu-Jitsu 2025"
                      required
                      id="championship-name" data-testid="championship-name"
                      className="bg-[#1A1A1A] border-[#27272A] text-white"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-[#A1A1AA]" htmlFor="championship-date">Data *</Label>
                    <Input
                      type="date"
                      value={formData.date}
                      onChange={(e) => handleChange('date', e.target.value)}
                      required
                      id="championship-date" data-testid="championship-date"
                      className="bg-[#1A1A1A] border-[#27272A] text-white"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-[#A1A1AA]" htmlFor="championship-payment-deadline">Data limite de pagamento</Label>
                    <Input
                      type="date"
                      value={formData.payment_deadline}
                      onChange={(e) => handleChange('payment_deadline', e.target.value)}
                      id="championship-payment-deadline" data-testid="championship-payment-deadline"
                      className="bg-[#1A1A1A] border-[#27272A] text-white"
                    />
                  </div>
                  
                  <div className="space-y-2 md:col-span-2">
                    <Label className="text-[#A1A1AA]" htmlFor="championship-address">Endereço *</Label>
                    <Input
                      value={formData.address}
                      onChange={(e) => handleChange('address', e.target.value)}
                      placeholder="Ex: Ginásio Municipal, São Paulo - SP"
                      required
                      id="championship-address" data-testid="championship-address"
                      className="bg-[#1A1A1A] border-[#27272A] text-white"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-[#A1A1AA]" htmlFor="championship-federation">Federação Organizadora</Label>
                    <Select 
                      value={formData.federation_id || "none"} 
                      onValueChange={(v) => handleChange('federation_id', v === "none" ? "" : v)}
                    >
                      <SelectTrigger 
                        id="championship-federation" data-testid="championship-federation"
                        className="bg-[#1A1A1A] border-[#27272A] text-white"
                      >
                        <SelectValue placeholder="Selecione..." />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                        <SelectItem value="none" className="text-white hover:bg-[#27272A]">Nenhuma</SelectItem>
                        {federations.map((fed) => (
                          <SelectItem key={fed._id} value={fed._id} className="text-white hover:bg-[#27272A]">
                            {fed.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Bloco A (S6) — o formulário diz se este campeonato entra no
                      Ranking Federativo e POR QUÊ (antes a tela silenciava). */}
                  <div
                    className={`space-y-2 md:col-span-2 rounded-lg border p-3 ${TONE_CLASSES[rankingParticipation.tone]}`}
                    data-testid="championship-ranking-participation"
                  >
                    <div className="flex items-center gap-2 text-sm font-bold">
                      <ShieldCheck size={16} /> {rankingParticipation.label}
                    </div>
                    <p className="text-xs mt-1 opacity-90">{rankingParticipation.reason}</p>
                    {snapshotFederationMismatch && (
                      <p className="text-xs mt-1 font-semibold text-amber-300">
                        Atenção: o snapshot congelado é de outra federação. Trocar a federação NÃO reescreve o passado — os pontos
                        já congelados seguem válidos e a regra nova vale para campeonatos futuros.
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label className="text-[#A1A1AA]" htmlFor="championship-status">Status</Label>
                    <Select 
                      value={formData.status} 
                      onValueChange={(v) => handleChange('status', v)}
                    >
                      <SelectTrigger 
                        id="championship-status" data-testid="championship-status"
                        className="bg-[#1A1A1A] border-[#27272A] text-white"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                        <SelectItem value="open" className="text-white hover:bg-[#27272A]">Inscrições Abertas</SelectItem>
                        <SelectItem value="closed" className="text-white hover:bg-[#27272A]">Inscrições Encerradas</SelectItem>
                        <SelectItem value="in_progress" className="text-white hover:bg-[#27272A]">Em Andamento</SelectItem>
                        <SelectItem value="finished" className="text-white hover:bg-[#27272A]">Finalizado</SelectItem>
                      </SelectContent>
                    </Select>

                    {/* FASE 2.1 — "Finalizado" NÃO é o mesmo que "premiado".
                        A medalha só nasce na premiação; sem ela, o Ranking
                        Federativo e a página pública ficam zerados. */}
                    {formData.status === 'finished' && (
                      <div
                        data-testid="status-finished-warning"
                        className={`rounded-lg border p-3 text-xs leading-relaxed ${
                          medalCount === 0
                            ? 'border-amber-500/40 bg-amber-500/10 text-amber-200'
                            : 'border-[#27272A] bg-[#1A1A1A] text-[#A1A1AA]'
                        }`}
                      >
                        {medalCount === 0 ? (
                          <>
                            <p className="font-semibold flex items-center gap-1">
                              <AlertTriangle size={12} /> Este campeonato ainda NÃO foi premiado.
                            </p>
                            <p className="mt-1">
                              Sem premiar, as medalhas NÃO aparecem no Ranking Federativo nem na
                              página pública — as colunas de medalha ficam zeradas, mesmo com o
                              status &ldquo;Finalizado&rdquo;.
                            </p>
                            <p className="mt-1">
                              O rótulo &ldquo;Finalizado&rdquo; apenas marca o campeonato; quem grava
                              as medalhas é a <strong>premiação</strong> (ação à parte).
                            </p>
                          </>
                        ) : medalCount > 0 ? (
                          <p>
                            Este campeonato já tem <strong>{medalCount}</strong> medalha(s)
                            registrada(s). Premiar de novo não duplica nada (a premiação é
                            idempotente) e chaves de Lançamento Direto são puladas.
                          </p>
                        ) : (
                          <p>
                            Não foi possível conferir agora se este campeonato já foi premiado.
                            Confirme na tela de <strong>Medalhas</strong> antes de considerar o
                            evento encerrado.
                          </p>
                        )}
                        {isEdit && (
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={handlePremiarAgora}
                            disabled={awarding}
                            data-testid="award-now-btn"
                            className="mt-2 h-7 text-xs border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10"
                          >
                            {awarding
                              ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                              : <Medal className="w-3 h-3 mr-1" />}
                            Premiar agora
                          </Button>
                        )}
                      </div>
                    )}
                  </div>

                  <CabecalhoSecao className="md:col-span-2" titulo="Pontuação e ranking" icone={Medal} />

                  <div className="space-y-2">
                    <Label className="text-[#A1A1AA]" htmlFor="championship-scoring-system">Sistema de Pontuacao</Label>
                    {federationSelected && (
                      <p className="text-xs text-amber-300">
                        Ignorado neste campeonato: com federação organizadora a escala de medalha vem da REGRA DA FEDERAÇÃO
                        {ruleHasSavedVersion ? ` (versão ${ruleVersion})` : ' (ainda sem regra salva — vale o padrão do sistema)'}
                        {' '}e é congelada no snapshot. Este campo só vale para campeonatos SEM federação.
                      </p>
                    )}
                    <Select 
                      value={formData.scoring_system_id || "none"} 
                      onValueChange={(v) => handleChange('scoring_system_id', v === "none" ? "" : v)}
                    >
                      <SelectTrigger 
                        disabled={federationSelected}
                        id="championship-scoring-system" data-testid="championship-scoring-system"
                        className="bg-[#1A1A1A] border-[#27272A] text-white"
                      >
                        <SelectValue placeholder="Selecione o sistema de pontuacao..." />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                        <SelectItem value="none" className="text-white hover:bg-[#27272A]">Nenhum</SelectItem>
                        {scoringSystems.map((ss) => (
                          <SelectItem key={ss._id} value={ss._id} className="text-white hover:bg-[#27272A]">
                            {ss.name} {ss.is_default ? '(Padrao)' : ''} — Ouro: {ss.gold_points} | Prata: {ss.silver_points} | Bronze: {ss.bronze_points}{ss.second_bronze_points != null ? ` | 2º Bronze: ${ss.second_bronze_points}` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2 md:col-span-2">
                    <div className="flex items-center justify-between gap-4 rounded-lg border border-[#27272A] bg-[#1A1A1A] p-4">
                      <div>
                        <Label className="text-white font-['Chivo'] text-sm" htmlFor="championship-second-bronze">Este campeonato terá o 2º Bronze</Label>
                        <p className="text-xs text-[#71717A] mt-1">
                          {federationSelected
                            ? `${formData.has_second_bronze ? 'LIGADO' : 'DESLIGADO'}: ${formData.has_second_bronze ? 'a premiação cria o 4º lugar (2º Bronze) em TODA chave de 4 ou mais atletas. O sistema considera SEMIFINAL toda luta cujo vencedor vai à FINAL — por isso as chaves de 5, 6, 10, 11 e 12 atletas também premiam 4. EXCEÇÃO: quando um dos finalistas chega à final apenas por passes livres (ex.: chave de 9 atletas), existe uma só semifinal real e a chave fica com 3 colocações' : 'a chave de 4+ gera apenas o 1º Bronze (3 colocações: ouro, prata e bronze)'}. O VALOR do 2º Bronze vem da regra da federação: ${ruleSecondBronze} ponto${ruleSecondBronze === 1 ? '' : 's'}${ruleSecondBronze > 0 ? '' : ' (não pontua hoje)'}. Este interruptor decide apenas se a colocação EXISTE — o valor é da federação, não deste formulário.`
                            : (selectedScoring && selectedScoring.second_bronze_points != null
                              ? `O 2º Bronze valerá ${selectedScoring.second_bronze_points} pontos (estilo CBJJ, sem disputa pelo 3º lugar). Vale para toda chave de 4 ou mais atletas — o sistema considera SEMIFINAL toda luta cujo vencedor vai à final. Exceção: quando um finalista chega à final apenas por passes livres (ex.: chave de 9 atletas), a chave fica com 3 colocações.`
                              : 'O 2º Bronze valerá 0 pontos (estilo CBJJ, sem disputa pelo 3º lugar). Vale para toda chave de 4 ou mais atletas — exceção: quando um finalista chega à final apenas por passes livres (ex.: chave de 9 atletas), a chave fica com 3 colocações.')}
                        </p>
                      </div>
                      <Switch
                        checked={formData.has_second_bronze}
                        onCheckedChange={(checked) => handleChange('has_second_bronze', checked)}
                        id="championship-second-bronze" data-testid="championship-second-bronze"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-[#A1A1AA]" htmlFor="championship-ranking-weight">Peso no Ranking Federativo</Label>
                    <Select
                      value={formData.ranking_weight || 'low'}
                      onValueChange={(v) => handleChange('ranking_weight', v)}
                    >
                      <SelectTrigger
                        id="championship-ranking-weight" data-testid="championship-ranking-weight"
                        className="bg-[#1A1A1A] border-[#27272A] text-white"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                        <SelectItem value="none" className="text-white hover:bg-[#27272A]">{weightOptionLabel('none')}</SelectItem>
                        <SelectItem value="low" className="text-white hover:bg-[#27272A]">{weightOptionLabel('low')}</SelectItem>
                        <SelectItem value="medium" className="text-white hover:bg-[#27272A]">{weightOptionLabel('medium')}</SelectItem>
                        <SelectItem value="high" className="text-white hover:bg-[#27272A]">{weightOptionLabel('high')}</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-[#71717A]">
                      Bônus ADITIVO deste campeonato no Ranking Federativo, somado ao total do atleta
                      (medalha + participação + peso + performance). Nunca multiplica nada.
                      {federationSelected
                        ? ` O VALOR de cada faixa é definido pela regra da federação (versão ${ruleVersion ?? '?'}): Baixo +${ruleWeightPoints.low} · Médio +${ruleWeightPoints.medium} · Alto +${ruleWeightPoints.high}.`
                        : ' Só tem efeito em campeonato COM federação organizadora — sem federação o peso não entra em nenhum cálculo.'}
                      {' '}“Sem peso” não soma nada. É congelado no snapshot quando o campeonato entra em andamento — alterar depois não reescreve o passado.
                    </p>
                    {federationSelected && (
                      <p className="text-xs font-semibold text-[#D4AF37]" data-testid="championship-weight-effective">
                        Peso efetivo hoje: {WEIGHT_LABELS[weightLevel]} (+{effectiveWeightPoints} ponto{effectiveWeightPoints === 1 ? '' : 's'}).
                        {' '}Este bônus é pago pela PRÓPRIA configuração da federação: ele NÃO depende da participação estar
                        ligada (funciona mesmo com participação em 0) e exige apenas que o atleta tenha pelo menos 1 luta efetiva
                        (BYE nunca conta). O peso nunca multiplica medalha, participação ou performance.
                      </p>
                    )}
                  </div>

                  <CabecalhoSecao className="md:col-span-2" titulo="Categorias e modalidade" icone={Users} />

                  <div className="space-y-2">
                    <Label className="text-[#A1A1AA]" htmlFor="championship-category-table">Tabela de Categorias de Peso</Label>
                    <Select 
                      value={formData.category_table_id || "none"} 
                      onValueChange={(v) => handleChange('category_table_id', v === "none" ? "" : v)}
                    >
                      <SelectTrigger 
                        id="championship-category-table" data-testid="championship-category-table"
                        className="bg-[#1A1A1A] border-[#27272A] text-white"
                      >
                        <SelectValue placeholder="Selecione a tabela de categorias..." />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                        <SelectItem value="none" className="text-white hover:bg-[#27272A]">Nenhuma</SelectItem>
                        {categoryTables.map((ct) => (
                          <SelectItem key={ct._id} value={ct._id} className="text-white hover:bg-[#27272A]">
                            {ct.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <CabecalhoSecao className="md:col-span-2" titulo="Sorteio de chaves" icone={Swords} />
                  <div className="md:col-span-2">
                    <Dica icone={false}>
                      Define como as chaves sao montadas ao gerar os brackets. Pode ser alterado a qualquer momento — vale para o proximo sorteio.
                    </Dica>
                  </div>

                  <CabecalhoSecao className="md:col-span-2" titulo="Modo de operação" icone={ShieldCheck} />
                  <div className="md:col-span-2">
                    <Dica icone={false}>
                      Manual: fluxo tradicional com folhas impressas e Mesa Central. Automatico: Placar Eletronico integrado (digita o Nº da chave e envia o resultado). Hibrido: os dois ao mesmo tempo.
                    </Dica>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-[#A1A1AA]" htmlFor="championship-results-mode">Modo de Operacao do Campeonato</Label>
                    <Select value={formData.results_mode || 'manual'} onValueChange={(v) => handleChange('results_mode', v)}>
                      <SelectTrigger id="championship-results-mode" data-testid="championship-results-mode" className="bg-[#1A1A1A] border-[#27272A] text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                        <SelectItem value="manual" className="text-white hover:bg-[#27272A]">Manual (folhas + Mesa Central)</SelectItem>
                        <SelectItem value="automatic" className="text-white hover:bg-[#27272A]">Automatico (Placar Eletronico)</SelectItem>
                        <SelectItem value="hybrid" className="text-white hover:bg-[#27272A]">Hibrido (os dois)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-[#A1A1AA]" htmlFor="championship-draw-mode">Modo do Sorteio</Label>
                    <Select value={formData.draw_mode || 'seeded'} onValueChange={(v) => handleChange('draw_mode', v)}>
                      <SelectTrigger id="championship-draw-mode" data-testid="championship-draw-mode" className="bg-[#1A1A1A] border-[#27272A] text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                        <SelectItem value="seeded" className="text-white hover:bg-[#27272A]">Por Critérios (Ranking + Equipe)</SelectItem>
                        <SelectItem value="random" className="text-white hover:bg-[#27272A]">Totalmente Aleatório</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-[#A1A1AA]" htmlFor="championship-modality">Modalidade</Label>
                    <Select value={formData.modality || 'gi'} onValueChange={(v) => handleChange('modality', v)}>
                      <SelectTrigger
                        id="championship-modality" data-testid="championship-modality"
                        className="bg-[#1A1A1A] border-[#27272A] text-white"
                        style={{ borderColor: isMisto ? 'rgba(16,185,129,0.7)' : '#52525B' }}
                      >
                        <span className={`mr-2 inline-block h-2 w-2 shrink-0 rounded-full ${isMisto ? 'bg-emerald-400' : 'bg-zinc-500'}`} />
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                        <SelectItem value="gi" className="text-white hover:bg-[#27272A]">Kimono (só GI)</SelectItem>
                        <SelectItem value="nogi" className="text-white hover:bg-[#27272A]">No-Gi (sem kimono)</SelectItem>
                        <SelectItem value="misto" className="text-white hover:bg-[#27272A]">Misto (Kimono + No-Gi no mesmo evento)</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className={`text-xs ${isMisto ? 'text-emerald-400' : 'text-zinc-500'}`}>
                      {isMisto
                        ? 'MISTO — chaves separadas por modalidade; as folhas Staff/PDF ganham os marcadores KIMONO e NO-GI para os staffs diferenciarem.'
                        : 'Modalidade única — as chaves seguem o comportamento atual (sem marcador nas folhas).'}
                    </p>
                    {isMisto && (
                      <div className="flex items-center justify-between gap-3 rounded-lg border border-[#27272A] bg-[#1A1A1A] p-3 mt-2">
                        <div>
                          <Label className="text-white text-sm" htmlFor="championship-combine-modality-ranking">Somar pontos de Kimono e No-Gi no ranking</Label>
                          <p className="text-xs text-[#71717A]">LIGADO: um ranking único somando as duas modalidades (padrão). DESLIGADO: rankings separados por modalidade (filtro nas telas de ranking).</p>
                        </div>
                        <Switch
                          checked={formData.combine_modality_ranking !== false}
                          onCheckedChange={(checked) => handleChange('combine_modality_ranking', checked)}
                          id="championship-combine-modality-ranking" data-testid="championship-combine-modality-ranking"
                        />
                      </div>
                    )}

                    {/* Bloco C — campeonato MISTO + federação: como o Ranking
                        Federativo trata a modalidade. “Rankings separados”
                        passou a valer de verdade (uma linha por atleta por
                        modalidade); “pontos somados” mantém uma linha só, com a
                        modalidade do evento. A decisão fica CONGELADA no
                        snapshot — por isso o aviso mostra o que está valendo
                        neste campeonato. */}
                    {isMisto && federationSelected && (
                      <div
                        className={`mt-2 rounded-lg border p-3 text-xs leading-relaxed ${formData.combine_modality_ranking === false ? TONE_CLASSES.ok : TONE_CLASSES.muted}`}
                        data-testid="championship-mixed-modality-ranking-warning"
                      >
                        <p className="flex items-center gap-2 font-bold">
                          {formData.combine_modality_ranking === false
                            ? <ShieldCheck size={16} />
                            : <AlertTriangle size={16} />}
                          Campeonato MISTO no Ranking Federativo
                        </p>
                        {formData.combine_modality_ranking === false ? (
                          <p className="mt-1">
                            <strong>Rankings separados:</strong> o Ranking Federativo separa este campeonato por modalidade — uma
                            linha por atleta POR MODALIDADE, resolvida pela chave de cada luta/medalha — e os filtros de Modalidade
                            (Kimono/No-Gi) funcionam de verdade no ranking da federação. Cada modalidade passa a contar como um
                            ranking próprio: o atleta que competiu nas DUAS recebe participação e peso em cada uma. Vale para
                            campeonatos que ainda NÃO têm snapshot congelado — quem já entrou em andamento antes continua como estava.
                          </p>
                        ) : (
                          <p className="mt-1">
                            <strong>Pontos somados (padrão):</strong> o evento entra como UMA linha por atleta, com a modalidade do
                            evento. No filtro de Modalidade do ranking público ele aparece em “Todas as modalidades” — não em “Kimono”
                            nem em “No-Gi”. Para separar Kimono e No-Gi no ranking da federação, desligue “Somar pontos de Kimono e
                            No-Gi”.
                          </p>
                        )}
                        {frozenModalityRankingLabel && (
                          <p className="mt-1 opacity-90" data-testid="championship-modality-ranking-frozen">
                            {frozenModalityRankingLabel}
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label className="text-[#A1A1AA]" htmlFor="championship-belt-rule">Regra de Competição por Faixa</Label>
                    <Select value={formData.belt_competition_rule || 'orange_green_combined'} onValueChange={(v) => handleChange('belt_competition_rule', v)}>
                      <SelectTrigger
                        id="championship-belt-rule" data-testid="championship-belt-rule"
                        className="bg-[#1A1A1A] border-[#27272A] text-white"
                        style={{ borderColor: beltRuleCombined ? 'rgba(16,185,129,0.7)' : '#52525B' }}
                      >
                        <span className={`mr-2 inline-block h-2 w-2 shrink-0 rounded-full ${beltRuleCombined ? 'bg-emerald-400' : 'bg-zinc-500'}`} />
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                        <SelectItem value="orange_green_combined" className="text-white hover:bg-[#27272A]">Laranja + Verde juntas (padrão)</SelectItem>
                        <SelectItem value="separated" className="text-white hover:bg-[#27272A]">Separar Laranja e Verde</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className={`text-xs ${beltRuleCombined ? 'text-emerald-400' : 'text-zinc-500'}`}>
                      {beltRuleCombined
                        ? 'LIGADO — Laranja e Verde competem na mesma categoria e a chave aparece como "Laranja/Verde" (o cadastro do atleta mantém a faixa real).'
                        : 'DESLIGADO — Laranja e Verde geram categorias e chaves separadas.'}
                    </p>
                  </div>

                  

<div className="space-y-2">
                    <Label className="text-[#A1A1AA]" htmlFor="championship-kids-enabled">Incluir Kids (agrupamento)</Label>
                    <Select
                      value={formData.kids_enabled ? 'sim' : 'nao'}
                      onValueChange={(v) => {
                        const enabled = v === 'sim';
                        setFormData(prev => ({
                          ...prev,
                          kids_enabled: enabled,
                          kids_max_age: enabled && !prev.kids_max_age ? 5 : prev.kids_max_age
                        }));
                      }}
                    >
                      <SelectTrigger
                        id="championship-kids-enabled" data-testid="championship-kids-enabled"
                        className="bg-[#1A1A1A] border-[#27272A] text-white"
                        style={{ borderColor: formData.kids_enabled ? 'rgba(16,185,129,0.7)' : '#52525B' }}
                      >
                        <span className={`mr-2 inline-block h-2 w-2 shrink-0 rounded-full ${formData.kids_enabled ? 'bg-emerald-400' : 'bg-zinc-500'}`} />
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                        <SelectItem value="nao" className="text-white hover:bg-[#27272A]">Não</SelectItem>
                        <SelectItem value="sim" className="text-white hover:bg-[#27272A]">Sim</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className={`text-xs ${formData.kids_enabled ? 'text-emerald-400' : 'text-zinc-500'}`}>
                      {formData.kids_enabled
                        ? 'KIDS ATIVO — o agrupamento agrega as categorias oficiais até a idade escolhida (consultas/relatórios). NÃO altera o Ranking Federativo nem as chaves: o ranking federado usa a categoria oficial do atleta.'
                        : 'Desativado — nenhuma categoria pertence ao agrupamento Kids.'}
                    </p>
                    {formData.kids_enabled && (
                      <div className="mt-2">
                        <Label className="text-white text-sm" htmlFor="championship-kids-max-age">Kids até:</Label>
                        <Select value={String(formData.kids_max_age || 5)} onValueChange={(v) => handleChange('kids_max_age', Number(v))}>
                          <SelectTrigger
                            id="championship-kids-max-age" data-testid="championship-kids-max-age"
                            className="mt-1 bg-[#1A1A1A] border-[#27272A] text-white"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                            <SelectItem value="5" className="text-white hover:bg-[#27272A]">4 a 5 anos</SelectItem>
                            <SelectItem value="7" className="text-white hover:bg-[#27272A]">4 a 7 anos</SelectItem>
                            <SelectItem value="9" className="text-white hover:bg-[#27272A]">4 a 9 anos</SelectItem>
                            <SelectItem value="11" className="text-white hover:bg-[#27272A]">4 a 11 anos</SelectItem>
                            <SelectItem value="13" className="text-white hover:bg-[#27272A]">4 a 13 anos</SelectItem>
                            <SelectItem value="15" className="text-white hover:bg-[#27272A]">4 a 15 anos</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-[#71717A] mt-1">
                          Pré-Mirim até Infanto-Juvenil B conforme a faixa escolhida. Juvenil, Adulto e Master nunca entram no agrupamento.
                        </p>
                      </div>
                    )}
                    <p className="text-xs text-[#71717A]">
                      O agrupamento Kids NÃO entra no Ranking Federativo: ele não altera pontuação, chaves nem categorias — vale para consultas e relatórios.
                    </p>
                  </div>

                  

<div className="space-y-2">
                    <Label className="text-[#A1A1AA]" htmlFor="championship-draw-ranking-source">Fonte do Ranking</Label>
                    <Select value={formData.draw_ranking_source || 'global'} onValueChange={(v) => handleChange('draw_ranking_source', v)}>
                      <SelectTrigger id="championship-draw-ranking-source" data-testid="championship-draw-ranking-source" className="bg-[#1A1A1A] border-[#27272A] text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                        <SelectItem value="global" className="text-white hover:bg-[#27272A]">Ranking Global Acumulado</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-[#71717A]">Filtro por campeonato (atleta/equipe) sera adicionado em breve.</p>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-[#A1A1AA]" htmlFor="championship-draw-priority">Prioridade</Label>
                    <Select value={formData.draw_priority || 'ranking'} onValueChange={(v) => handleChange('draw_priority', v)}>
                      <SelectTrigger id="championship-draw-priority" data-testid="championship-draw-priority" className="bg-[#1A1A1A] border-[#27272A] text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                        <SelectItem value="ranking" className="text-white hover:bg-[#27272A]">Ranking primeiro</SelectItem>
                        <SelectItem value="team" className="text-white hover:bg-[#27272A]">Separação por equipe primeiro</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-[#A1A1AA]" htmlFor="championship-draw-seeds">Cabeças de Chave (Seeds)</Label>
                    <Select value={formData.draw_seeds || 'quarter'} onValueChange={(v) => handleChange('draw_seeds', v)}>
                      <SelectTrigger id="championship-draw-seeds" data-testid="championship-draw-seeds" className="bg-[#1A1A1A] border-[#27272A] text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                        <SelectItem value="quarter" className="text-white hover:bg-[#27272A]">Top 1/4 da chave (padrão IBJJF)</SelectItem>
                        <SelectItem value="half" className="text-white hover:bg-[#27272A]">Top 1/2 da chave</SelectItem>
                        <SelectItem value="none" className="text-white hover:bg-[#27272A]">Sem cabeças de chave</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>

              {/* Bloco A (S1) — a regra que o Ranking Federativo REALMENTE
                  aplica neste campeonato. Somente leitura: a federação é a
                  dona das regras (tela de Regras do Ranking Federativo). */}
              {federationSelected && (
                <Card className="bg-[#141414] border-[#27272A]" data-testid="championship-federation-rules-readonly">
                  <CardHeader>
                    <CardTitle className="text-lg text-white font-['Chivo'] flex items-center gap-2">
                      <Medal size={20} className="text-[#D4AF37]" />
                      Regra do Ranking Federativo aplicada (somente leitura)
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-sm text-[#A1A1AA]">
                      {selectedFederation ? selectedFederation.name : 'Federação selecionada'} ·{' '}
                      {ruleHasSavedVersion
                        ? `regra vigente versão ${ruleVersion}`
                        : 'esta federação ainda não salvou uma regra (vale o padrão do sistema)'}.
                      {' '}Quem define os pontos deste campeonato é esta regra — e ela é CONGELADA no snapshot quando o campeonato entra em andamento.
                    </p>
                    {ruleConfig ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                        <div className="rounded-lg border border-[#27272A] bg-[#1A1A1A] p-3">
                          <p className="text-xs uppercase tracking-wider text-[#71717A] mb-1">Pontos por medalha</p>
                          <p className="text-white">
                            Ouro {ruleConfig.medal_points?.gold ?? 0} · Prata {ruleConfig.medal_points?.silver ?? 0} ·{' '}
                            Bronze {ruleConfig.medal_points?.bronze ?? 0} · 2º Bronze {ruleSecondBronze}
                          </p>
                        </div>
                        <div className="rounded-lg border border-[#27272A] bg-[#1A1A1A] p-3">
                          <p className="text-xs uppercase tracking-wider text-[#71717A] mb-1">Participação</p>
                          <p className="text-white">
                            {ruleConfig.participation_points ?? 0} ponto{ruleConfig.participation_points === 1 ? '' : 's'} por atleta por campeonato
                            {(ruleConfig.participation_points ?? 0) === 0 ? ' (desativada)' : ' — exige pelo menos 1 luta efetiva'}
                          </p>
                        </div>
                        <div className="rounded-lg border border-[#27272A] bg-[#1A1A1A] p-3">
                          <p className="text-xs uppercase tracking-wider text-[#71717A] mb-1">Bônus de performance (por tipo de vitória)</p>
                          <p className="text-white">
                            Vitória por pontos → {ruleConfig.per_fight_points ?? 0}
                            {(ruleConfig.per_fight_points ?? 0) === 0 ? ' (desativado)' : ''} ·{' '}
                            Vitória por finalização → {ruleConfig.submission_bonus_points ?? 0}
                            {(ruleConfig.submission_bonus_points ?? 0) === 0 ? ' (desativado)' : ''} · Derrota → 0
                          </p>
                        </div>
                        <div className="rounded-lg border border-[#27272A] bg-[#1A1A1A] p-3">
                          <p className="text-xs uppercase tracking-wider text-[#71717A] mb-1">Peso do campeonato (aditivo)</p>
                          <p className="text-white">Baixo +{ruleWeightPoints.low} · Médio +{ruleWeightPoints.medium} · Alto +{ruleWeightPoints.high}</p>
                          <p className="text-xs text-[#71717A] mt-1">
                            Pago pela própria regra (independe da participação) e apenas com pelo menos 1 luta efetiva do atleta.
                          </p>
                        </div>
                        <div className="rounded-lg border border-[#27272A] bg-[#1A1A1A] p-3">
                          <p className="text-xs uppercase tracking-wider text-[#71717A] mb-1">Agregação e WO</p>
                          <p className="text-white">
                            {aggregationLabel}
                            {' '}· WO conta como luta para contagem/participação: {includeWalkovers ? 'Sim' : 'Não'}
                            {' '}(BYE nunca conta).
                          </p>
                        </div>
                        <div className="rounded-lg border border-[#27272A] bg-[#1A1A1A] p-3">
                          <p className="text-xs uppercase tracking-wider text-[#71717A] mb-1 flex items-center gap-1">
                            <Users size={12} /> Ranking das Equipes
                          </p>
                          <p className="text-white">{teamSourceLabel}</p>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-[#71717A]">Carregando a regra da federação...</p>
                    )}
                    <div className="flex flex-wrap items-center gap-3">
                      {canEditRules && (
                        <Link
                          to={`/ranking-federativo/regras?federationId=${formData.federation_id}`}
                          className="inline-flex items-center gap-1 text-sm text-[#D4AF37] hover:underline"
                          data-testid="championship-open-federation-rules"
                        >
                          <Swords size={16} /> Abrir Regras do Ranking Federativo
                        </Link>
                      )}
                      <span className="text-xs text-[#71717A]">
                        Esta tela é apenas leitura — a federação é a dona das regras e alterações criam uma nova versão.
                      </span>
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Details Tab */}
            <TabsContent value="details" className="mt-6">
              <Card className="bg-[#141414] border-[#27272A]">
                <CardHeader>
                  <CardTitle className="text-lg text-white font-['Chivo']">Detalhes do Evento</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2 md:col-span-2">
                    <Label className="text-[#A1A1AA]" htmlFor="championship-about">Sobre o Evento</Label>
                    <Textarea
                      value={formData.about_event}
                      onChange={(e) => handleChange('about_event', e.target.value)}
                      placeholder="Descricao geral do evento, objetivo, organizacao..."
                      id="championship-about" data-testid="championship-about"
                      className="bg-[#1A1A1A] border-[#27272A] text-white min-h-[120px]"
                    />
                  </div>

                  <div className="space-y-2 md:col-span-2">
                    <Label className="text-[#A1A1AA]" htmlFor="championship-location">Local do Evento</Label>
                    <Textarea
                      value={formData.event_location}
                      onChange={(e) => handleChange('event_location', e.target.value)}
                      placeholder="Detalhes do local: ginasio, endereco completo, como chegar, estacionamento..."
                      id="championship-location" data-testid="championship-location"
                      className="bg-[#1A1A1A] border-[#27272A] text-white min-h-[100px]"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-[#A1A1AA]" htmlFor="championship-values">Valores das Inscricoes</Label>
                    <Textarea
                      value={formData.registration_values}
                      onChange={(e) => handleChange('registration_values', e.target.value)}
                      placeholder="Ex: Categoria: R$ 80,00 | Absoluto: R$ 50,00"
                      id="championship-values" data-testid="championship-values"
                      className="bg-[#1A1A1A] border-[#27272A] text-white min-h-[100px]"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-[#A1A1AA]" htmlFor="championship-payment-methods">Formas de Pagamento</Label>
                    <Textarea
                      value={formData.payment_methods}
                      onChange={(e) => handleChange('payment_methods', e.target.value)}
                      placeholder="Ex: PIX, Cartao de Credito, Boleto"
                      id="championship-payment-methods" data-testid="championship-payment-methods"
                      className="bg-[#1A1A1A] border-[#27272A] text-white min-h-[100px]"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-[#A1A1AA]" htmlFor="championship-prizes">Premiacao</Label>
                    <Textarea
                      value={formData.prizes}
                      onChange={(e) => handleChange('prizes', e.target.value)}
                      placeholder="Ex: Medalhas para 1o, 2o e 3o lugar"
                      id="championship-prizes" data-testid="championship-prizes"
                      className="bg-[#1A1A1A] border-[#27272A] text-white min-h-[100px]"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-[#A1A1AA]" htmlFor="championship-categories">Categorias</Label>
                    <Textarea
                      value={formData.categories_info}
                      onChange={(e) => handleChange('categories_info', e.target.value)}
                      placeholder="Informacoes sobre as categorias do campeonato..."
                      id="championship-categories" data-testid="championship-categories"
                      className="bg-[#1A1A1A] border-[#27272A] text-white min-h-[100px]"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-[#A1A1AA]" htmlFor="championship-absolute">Absoluto</Label>
                    <Textarea
                      value={formData.absolute_info}
                      onChange={(e) => handleChange('absolute_info', e.target.value)}
                      placeholder="Informacoes sobre a categoria absoluto..."
                      id="championship-absolute" data-testid="championship-absolute"
                      className="bg-[#1A1A1A] border-[#27272A] text-white min-h-[100px]"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-[#A1A1AA]" htmlFor="championship-checkin">Checagem / Check-in</Label>
                    <Textarea
                      value={formData.checkin_info}
                      onChange={(e) => handleChange('checkin_info', e.target.value)}
                      placeholder="Informacoes sobre checagem e check-in..."
                      id="championship-checkin" data-testid="championship-checkin"
                      className="bg-[#1A1A1A] border-[#27272A] text-white min-h-[100px]"
                    />
                    {/* FASE 7.5 — decisão registrada: o check-in é TEXTO
                        informativo. Não existe operação de check-in no sistema. */}
                    <Dica icone={false}>
                      Texto <strong className="text-[#A1A1AA]">informativo</strong> exibido ao público.
                      Não há operação de check-in no sistema hoje (a presença é conferida manualmente na
                      mesa) — nada nas chaves, no placar, no telão ou no ranking depende deste campo.
                    </Dica>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-[#A1A1AA]" htmlFor="championship-brackets">Chaves</Label>
                    <Textarea
                      value={formData.brackets_info}
                      onChange={(e) => handleChange('brackets_info', e.target.value)}
                      placeholder="Informacoes sobre as chaves do campeonato..."
                      id="championship-brackets" data-testid="championship-brackets"
                      className="bg-[#1A1A1A] border-[#27272A] text-white min-h-[100px]"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-[#A1A1AA]" htmlFor="championship-weighin">Pesagem</Label>
                    <Textarea
                      value={formData.weigh_in_info}
                      onChange={(e) => handleChange('weigh_in_info', e.target.value)}
                      placeholder="Informacoes sobre pesagem (horario, local, tolerancia)..."
                      id="championship-weighin" data-testid="championship-weighin"
                      className="bg-[#1A1A1A] border-[#27272A] text-white min-h-[100px]"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-[#A1A1AA]" htmlFor="championship-rules">Regras do Campeonato</Label>
                    <Textarea
                      value={formData.rules}
                      onChange={(e) => handleChange('rules', e.target.value)}
                      placeholder="Descreva as regras do campeonato..."
                      id="championship-rules" data-testid="championship-rules"
                      className="bg-[#1A1A1A] border-[#27272A] text-white min-h-[100px]"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-[#A1A1AA]" htmlFor="championship-image-rights">Direito de Imagem</Label>
                    <Textarea
                      value={formData.image_rights}
                      onChange={(e) => handleChange('image_rights', e.target.value)}
                      placeholder="Termo de cessao de direito de imagem..."
                      id="championship-image-rights" data-testid="championship-image-rights"
                      className="bg-[#1A1A1A] border-[#27272A] text-white min-h-[100px]"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-[#A1A1AA]" htmlFor="championship-health">Declaracao de Saude</Label>
                    <Textarea
                      value={formData.health_declaration}
                      onChange={(e) => handleChange('health_declaration', e.target.value)}
                      placeholder="Declaracao de responsabilidade e condicoes de saude..."
                      id="championship-health" data-testid="championship-health"
                      className="bg-[#1A1A1A] border-[#27272A] text-white min-h-[100px]"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-[#A1A1AA]" htmlFor="championship-schedule">Cronograma</Label>
                    <Textarea
                      value={formData.schedule}
                      onChange={(e) => handleChange('schedule', e.target.value)}
                      placeholder="Ex: 8h Pesagem | 9h Inicio das Lutas"
                      id="championship-schedule" data-testid="championship-schedule"
                      className="bg-[#1A1A1A] border-[#27272A] text-white min-h-[100px]"
                    />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Media Tab */}
            <TabsContent value="media" className="mt-6">
              <Card className="bg-[#141414] border-[#27272A]">
                <CardHeader>
                  <CardTitle className="text-lg text-white font-['Chivo']">Imagem do Campeonato</CardTitle>
                </CardHeader>
                <CardContent className="space-y-8">
                  <SecaoFormulario
                    titulo="Banner / Card do Campeonato"
                    icone={Image}
                    dica="Esta imagem será exibida na página pública de campeonatos. Recomendado: 1200x400 pixels."
                  >
                    <ImageUpload
                      entityType="championship"
                      entityId={isEdit ? id : null}
                      currentPath={formData.banner_path}
                      onUpload={handleImageUpload}
                      shape="banner"
                      label="Upload Banner"
                    />
                  </SecaoFormulario>
                  <SecaoFormulario
                    titulo="Tabela de Pesos (Imagem ou PDF)"
                    icone={FileText}
                    dica="Tabela oficial de categorias e pesos do campeonato. Será disponibilizada para o público através de um botão na página do evento. Aceita JPG, PNG, GIF, WebP ou PDF (até 10MB para PDF)."
                  >
                    <ImageUpload
                      entityType="championship"
                      entityId={isEdit ? id : null}
                      currentPath={formData.weight_table_path}
                      onUpload={handleWeightTableUpload}
                      shape="banner"
                      label="Upload Tabela"
                      acceptPdf
                      field="weight_table_path"
                    />
                  </SecaoFormulario>
                  <SecaoFormulario
                    titulo="Edital do Campeonato (PDF)"
                    icone={FileText}
                    dica="Edital / regulamento oficial do campeonato. Será disponibilizado para o público através de um botão na página do evento. Aceita PDF (até 10MB)."
                  >
                    <ImageUpload
                      entityType="championship"
                      entityId={isEdit ? id : null}
                      currentPath={formData.edital_path}
                      onUpload={handleEditalUpload}
                      shape="banner"
                      label="Upload Edital"
                      acceptPdf
                      field="edital_path"
                    />
                  </SecaoFormulario>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          {/* Actions — barra fixa no rodapé: as abas são longas e o "Salvar"
              ficava a ~3 telas de distância do topo. */}
          <BarraAcoes contexto="pagina">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/championships')}
              className="border-[#27272A] text-[#A1A1AA] hover:text-white"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={saving}
              data-testid="save-championship"
              className="bg-[#D4AF37] text-black hover:bg-[#E5C158] font-bold"
            >
              {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save size={20} className="mr-2" />}
              {isEdit ? 'Salvar Alterações' : 'Criar Campeonato'}
            </Button>
          </BarraAcoes>
        </form>
      </div>
    </MainLayout>
  );
}
